import { Button, InputImperativeActions, Search } from '@wix/design-system'
import { useContent, useExperiments, useMetadata, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import cs from 'classnames'
import React, { FC, forwardRef, useCallback, useEffect, useReducer, useRef } from 'react'

import { useDebouncedTypingLogger, usePageLogger } from '../../../bi/mfi'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { debounce } from '../../../utils/debounce'
import { BusinessTermService } from '../../reusable/Category/api/BusinessTermService'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobileInput } from '../../reusable/WixMobileInput'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import { goNext } from './state/actions/goNext'
import { loadCategories } from './state/actions/loadCategories'
import { selectCategory } from './state/actions/selectCategory'
import { getInitialState, searchStateReducer } from './state/searchStateReducer'
import { SearchActionType, SearchScreen } from './state/types'
import { WixMobileCategoriesSearchResults } from './WixMobileCategoriesSearchResults/WixMobileCategoriesSearchResults'
import { TWixMobileCategory as Content } from './WixMobileCategory.content'
import { wixMobileCategoryDataHooks as dataHooks } from './WixMobileCategory.dataHooks'
import s from './WixMobileCategory.scss'
import { WixMobileCategorySuggestions } from './WixMobileCategorySuggestions/WixMobileCategorySuggestions'

const debouncedLoadCategories = debounce(loadCategories, 400)

interface WixMobileCategoryProps {
  businessTermService: BusinessTermService
}

const InputWithKeyHintNext = forwardRef((props, ref) => {
  // @ts-expect-error
  return <input {...props} ref={ref} enterkeyhint="next" />
})

export const WixMobileCategory: FC<WixMobileCategoryProps> = ({ businessTermService }) => {
  const { save, category: savedCategory } = useStorageContext()
  const { content } = useContent<Content>()
  const pageLogger = usePageLogger()
  const debouncedTyping = useDebouncedTypingLogger(pageLogger)
  const answerTypeRef = useRef<'chips' | 'free text'>('free text')
  const [pageLoading] = usePageLoading()
  const [{ language }] = useMetadata()
  const next = useNext()
  const { questionKey } = content.question
  const experiments = useExperiments()
  const isHarmony = content.skin === 'harmony'

  const onNextButtonClick = () => {
    if (pageLoading) {
      return
    }

    goNext({
      next,
      save,
      category: searchState.selectedCategory,
      searchValue: searchState.searchValue,
      questionKey,
      language,
      pageLogger,
      experiments,
      answerType: answerTypeRef.current,
    })
  }

  const onBackButtonClick = () => {
    if (pageLoading) {
      return
    }

    if (searchState.screen !== SearchScreen.INITIAL) {
      dispatchSearchStateAction({ type: SearchActionType.TO_INITIAL_SCREEN })
      return
    }

    void pageLogger.back()
    window.history.back()
  }

  const onSkipButtonClick = () => {
    if (pageLoading) {
      return
    }
    void pageLogger.skip()
    next({ [questionKey]: { skipped: true } })
  }

  const searchRef = useRef<Search>(null)
  const harmonyInputRef = useRef<InputImperativeActions>(null)
  const searchWrapperRef = useRef<HTMLDivElement | null>(null)

  const [searchState, dispatchSearchStateAction] = useReducer(searchStateReducer, getInitialState({ savedCategory }))

  const focusSearch = () => {
    if (isHarmony) {
      harmonyInputRef.current?.focus()
    } else {
      searchRef.current?.focus()
    }

    dispatchSearchStateAction({ type: SearchActionType.SEARCH_FOCUS })
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const blurInput = () => {
    if (isHarmony) {
      harmonyInputRef.current?.blur()
    } else {
      searchRef.current?.blur()
    }

    dispatchSearchStateAction({ type: SearchActionType.SEARCH_BLUR })
  }

  const touchStartPosY = useRef<number>(0)
  const scrollDetectionHandler = useCallback((e: TouchEvent) => {
    if (e.target instanceof HTMLElement && searchWrapperRef.current?.contains(e.target)) {
      return
    }

    // Different devices give different values with different decimal percentages.
    const currentTouchPosY = Math.round(e.changedTouches[0].screenY)
    if (touchStartPosY.current === currentTouchPosY) {
      return
    }

    if (touchStartPosY.current - currentTouchPosY !== 0) {
      blurInput()
    }
    touchStartPosY.current = currentTouchPosY

    blurInput()
  }, [])

  useEffect(() => {
    if (searchState.isSearchFocused) {
      window.document.addEventListener('touchmove', scrollDetectionHandler)
    } else {
      window.document.removeEventListener('touchmove', scrollDetectionHandler)
    }

    return () => window.document.removeEventListener('touchmove', scrollDetectionHandler)
  }, [searchState.isSearchFocused, scrollDetectionHandler])

  return (
    <WixMobilePage>
      <WixMobileHeader
        skin={isHarmony ? 'harmony' : 'standard'}
        leftContent={<WixMobileBackButton dataHook={dataHooks.back()} onBack={onBackButtonClick} />}
        rightContent={
          (content.skip && (
            <WixMobileSkipButton dataHook={dataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
          )) ||
          undefined
        }
      />
      <WixMobileContentWrapper>
        {searchState.screen === SearchScreen.INITIAL && (
          <>
            {content.stepProgressBar.content.visible && (
              <WixMobileStepProgressBar
                current={content.stepProgressBar.content.current}
                total={content.stepProgressBar.content.total}
                skin={isHarmony ? 'harmony' : 'default'}
                dataHook={dataHooks.stepProgressBar()}
              />
            )}
            <WixMobileHeading dataHook={dataHooks.title()} skin={isHarmony ? 'harmony' : 'default'}>
              {content.question.content.title}
            </WixMobileHeading>
            {content.question.content.subtitle && (
              <WixMobileDescription dataHook={dataHooks.subtitle()} skin={isHarmony ? 'harmony' : 'default'}>
                {content.question.content.subtitle}
              </WixMobileDescription>
            )}
          </>
        )}
        <div
          ref={ref => {
            searchWrapperRef.current = ref
          }}
        >
          {isHarmony ? (
            <WixMobileInput
              placeholder={content.question.content.placeholder}
              value={searchState.searchValue}
              ref={harmonyInputRef}
              onChange={async e => {
                const value = e.target.value
                answerTypeRef.current = 'free text'
                debouncedTyping(value)
                dispatchSearchStateAction({ type: SearchActionType.SEARCH_CHANGE, value })
                await debouncedLoadCategories({
                  businessTermService,
                  dispatch: dispatchSearchStateAction,
                  query: value,
                  language,
                })
              }}
              onFocus={() => {
                dispatchSearchStateAction({ type: SearchActionType.SEARCH_FOCUS })
              }}
              onBlur={() => {
                dispatchSearchStateAction({ type: SearchActionType.SEARCH_BLUR })
              }}
              onClear={() => {
                answerTypeRef.current = 'free text'
                void pageLogger.generalAction({ action: 'delete_category' })
                dispatchSearchStateAction({ type: SearchActionType.SEARCH_EMPTY })
              }}
              status={searchState.isCategoriesLoading ? 'loading' : undefined}
              clearButton
            />
          ) : (
            <Search
              size="large"
              placeholder={content.question.content.placeholder}
              value={searchState.searchValue}
              ref={searchRef}
              onChange={async e => {
                const value = e.target.value
                answerTypeRef.current = 'free text'
                debouncedTyping(value)
                dispatchSearchStateAction({ type: SearchActionType.SEARCH_CHANGE, value })
                await debouncedLoadCategories({
                  businessTermService,
                  dispatch: dispatchSearchStateAction,
                  query: value,
                  language,
                })
              }}
              onFocus={() => {
                dispatchSearchStateAction({ type: SearchActionType.SEARCH_FOCUS })
              }}
              onBlur={() => {
                dispatchSearchStateAction({ type: SearchActionType.SEARCH_BLUR })
              }}
              onClear={() => {
                answerTypeRef.current = 'free text'
                void pageLogger.generalAction({ action: 'delete_category' })
                dispatchSearchStateAction({ type: SearchActionType.SEARCH_EMPTY })
              }}
              status={searchState.isCategoriesLoading ? 'loading' : undefined}
              className={s.searchInput}
              customInput={InputWithKeyHintNext}
            />
          )}
        </div>
        {searchState.screen === SearchScreen.SEARCH_CATEGORIES_RESULTS_ACTIVE && searchState.categories.length > 0 && (
          <WixMobileCategoriesSearchResults
            categories={searchState.categories}
            onCategoryClick={async (businessTerm, index) => {
              focusSearch()
              await selectCategory({
                businessTerm,
                dispatch: dispatchSearchStateAction,
                businessTermService,
                questionKey,
                pageLogger,
                index,
              })
              await loadCategories({
                businessTermService,
                dispatch: dispatchSearchStateAction,
                query: businessTerm.displayName || '',
                language,
              })
            }}
          />
        )}
        {[SearchScreen.INITIAL, SearchScreen.SEARCH_SUGGESTIONS_ACTIVE].includes(searchState.screen) && (
          <WixMobileCategorySuggestions
            suggestions={content.question.content.suggestions.items}
            onSuggestionClick={async (suggestion, index) => {
              answerTypeRef.current = 'chips'
              dispatchSearchStateAction({ type: SearchActionType.SEARCH_SELECT_SUGGESTION, suggestion })
              void pageLogger.clickAnswer({
                questionKey,
                buttonName: suggestion,
                suggestedOption: true,
                position: index + 1,
              })
              focusSearch()
              await loadCategories({
                businessTermService,
                dispatch: dispatchSearchStateAction,
                query: suggestion,
                language,
              })
            }}
            skin={isHarmony ? 'harmony' : 'default'}
            dataHook={dataHooks.suggestions()}
          />
        )}
      </WixMobileContentWrapper>
      {content.next && (
        <WixMobileFooter>
          <Button
            fullWidth
            onClick={onNextButtonClick}
            dataHook={dataHooks.next()}
            disabled={!searchState.selectedCategory}
            size="large"
            className={cs({ [s.harmonyNextButton]: isHarmony })}
          >
            {content.next}
          </Button>
        </WixMobileFooter>
      )}
    </WixMobilePage>
  )
}
